import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { switchUserLogin, logout } from 'redux/actions/user';
import { ACTION_USER_SAVE } from 'redux/actions/actionTypes';
import { URLS } from 'common/urls';
import jrnlPreloader from 'assets/images/preloader.gif';

const SwitchUserPage = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const exchangeToken = async () => {
            try {
                // Get token from query params
                const params = new URLSearchParams(location.search);
                const token = params.get('token');

                if (!token) {
                    setError('Invalid token. Please try again.');
                    setLoading(false);
                    return;
                }

                // IMPORTANT: Logout current user first to clear all state/storage
                // This prevents flickering between old and new user data
                await dispatch(logout());

                // Exchange token for credentials
                const response = await dispatch(switchUserLogin(token));

                if (response.error) {
                    setError(
                        response.error.response?.data?.error ||
                            'Failed to authenticate. Token may have expired.'
                    );
                    setLoading(false);
                    return;
                }

                const {
                    user,
                    api_key,
                    api_v2_key,
                    is_su,
                    su_admin_email,
                    su_audit_log_id,
                    su_session_expires_at
                } = response.payload.data;

                // Flatten user data to match expected format
                const userData = {
                    ...user,
                    api_key,
                    api_v2_key,
                    is_su,
                    su_admin_email,
                    su_audit_log_id,
                    su_session_expires_at,
                    // Ensure required auth fields are present
                    id: user.id || user.pk,
                    username: user.username,
                    email: user.email
                };

                // Store in Redux storage middleware format: { user: {...} }
                const storageData = { user: userData };
                localStorage.setItem('jrnluser', JSON.stringify(storageData));

                // Dispatch to Redux store with ACTION_USER_SAVE
                dispatch({
                    type: ACTION_USER_SAVE,
                    payload: userData
                });

                // Small delay to ensure state is updated
                setTimeout(() => {
                    // Redirect to timeline
                    history.push(URLS.TIMELINE);
                }, 100);
            } catch (err) {
                console.error('Switch user error:', err);
                setError('An unexpected error occurred. Please try again.');
                setLoading(false);
            }
        };

        exchangeToken();
    }, [dispatch, history, location.search]);

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    backgroundColor: '#f5f5f5'
                }}>
                <img
                    src={jrnlPreloader}
                    alt="Loading..."
                    style={{ width: '64px', height: '64px' }}
                />
                <p
                    style={{
                        marginTop: '20px',
                        fontSize: '16px',
                        color: '#666'
                    }}>
                    Authenticating as user...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    backgroundColor: '#f5f5f5',
                    padding: '20px'
                }}>
                <div
                    style={{
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        maxWidth: '500px',
                        textAlign: 'center'
                    }}>
                    <span
                        role="img"
                        aria-label="warning"
                        style={{
                            fontSize: '48px',
                            color: '#ff6b6b',
                            marginBottom: '20px'
                        }}>
                        ⚠️
                    </span>
                    <h2 style={{ color: '#333', marginBottom: '15px' }}>
                        Authentication Failed
                    </h2>
                    <p style={{ color: '#666', marginBottom: '25px' }}>
                        {error}
                    </p>
                    <button
                        onClick={() => (window.location.href = URLS.LOGIN)}
                        style={{
                            padding: '12px 30px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '16px',
                            cursor: 'pointer'
                        }}>
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default SwitchUserPage;
